import '@/styles/tailwind-core.module.css';
import { Bai_Jamjuree, Black_Han_Sans } from 'next/font/google';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import Footer from '@/common/footer';
import Header from '@/common/header';
import NextSeoComponent from '@/layouts/next-seo-component';
import logo from '@/public/assets/images/game/fortnite/fortnite-logo.webp';
import imageStepOne from '@/public/assets/images/game/fortnite/step1.webp';
import imageStepTwo from '@/public/assets/images/game/fortnite/step2.webp';
import imageStepThree from '@/public/assets/images/game/fortnite/step3.webp';
import imageStepFour from '@/public/assets/images/game/fortnite/step4.webp';
import { Main } from '@/templates/main';
import { buildCanonical } from '@/utils/seo';

import { bakbakOne } from './dota2';

const blackHanSans = Black_Han_Sans({
  subsets: ['latin'],
  weight: '400',
  display: 'swap',
  variable: '--font-blackHanSans',
});

const baiJamjuree = Bai_Jamjuree({
  subsets: ['latin'],
  weight: '400',
  display: 'swap',
  variable: '--font-baiJamjuree',
});

export default function FortnitePage() {
  const router = useRouter();
  const { t } = useTranslation(['pages/fortnite', 'components/titles']);

  return (
    <>
      <Header variant='primary' />

      <Main meta={<NextSeoComponent title={t('fortnite', { ns: 'components/titles' })} description={t('fortnite.description')} canonical={buildCanonical(router.locale, '/fortnite')} structuredDataType='software' breadcrumbs={[{ name: t('fortnite', { ns: 'components/titles' }), item: buildCanonical(router.locale, '/fortnite') }]} />}>
        <div className="flex justify-center bg-[url('/assets/images/game/fortnite/bg_top.webp')] bg-cover bg-top bg-no-repeat p-10 xl:pt-20">
          <div className='flex w-full max-w-[1487px] gap-5 xl:gap-14'>
            <div className='hidden flex-col items-center gap-8 xl:flex'>
              <Image src={logo} alt='Fortnite' />
            </div>

            <div className='mt-5 xl:mt-24'>
              <h1 className={`mb-5 max-w-[528px] font-bold uppercase ${blackHanSans.variable} font-black_han_sans text-[40px] font-bold leading-12 text-white xl:mb-16`}>{t('fortnite.title')}</h1>

              <figure className='mb-5 xl:mb-12'>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption -- the steps below (#how-to-steps) are the text alternative */}
                <video preload='none' width='600' controls muted aria-describedby='how-to-steps'>
                  <source src='/assets/video/fortnite/video.mp4' type='video/mp4' />
                </video>
                <figcaption className='mt-2 max-w-[600px] font-bai_jamjuree text-sm font-medium text-white/90'>{t('fortnite.video_caption')}</figcaption>
              </figure>

              <div className='flex items-start justify-start gap-4'>
                <span className='mt-1 size-sm min-h-3.5 min-w-3.5 bg-[#77DFF9]' />
                <div className='flex flex-col gap-5 font-bai_jamjuree text-base font-medium text-white'>
                  <p className='w-full max-w-[528px] leading-5'>{t('fortnite.description')}</p>

                  <p className='w-full max-w-[1031px] leading-[1.375rem]'>{t('fortnite.description_two')}</p>

                  <p className='w-full max-w-[1031px] leading-[1.375rem]'>{t('fortnite.description_three')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center bg-[url('/assets/images/game/fortnite/bg_bottom.webp')] bg-cover p-5 xl:pb-28 xl:pt-11">
          <div className='flex w-full max-w-[1500px] gap-5 xl:gap-14'>
            <div className='hidden flex-col items-center gap-11 opacity-0 xl:flex'>
              <Image src={logo} alt='' />
              <hr className='flex h-60 w-[2px] bg-[#5A2800]' />
            </div>

            <div id='how-to-steps' className='grid grid-cols-1 gap-5 xl:grid-cols-3 xl:gap-x-40 xl:gap-y-32'>
              <div className='flex w-full flex-col xl:max-w-[372px]'>
                <div className='mb-5 flex items-center justify-start gap-4'>
                  <span className='size-sm min-h-3.5 min-w-3.5 bg-[#77DFF9]' />
                  <p className={`w-full max-w-[528px] ${blackHanSans.variable} font-black_han_sans text-xl font-bold leading-4 text-[#77DFF9]`}>1</p>
                </div>

                <Image className='mb-5' src={imageStepOne} alt={t('fortnite.first_step_image_alt')} quality={100} />
                <p className='w-full max-w-[528px] font-bai_jamjuree text-base font-bold leading-4 text-[#77DFF9]'>{t('fortnite.first_step_title')}</p>

                <div className='my-5 flex items-start justify-start gap-4'>
                  <span className='size-4 bg-[#77DFF9]' />
                  <p className='w-full max-w-[528px] font-bai_jamjuree text-base font-medium leading-6 text-white'>{t('fortnite.first_step_text')}</p>
                </div>
              </div>

              <div className='flex w-full flex-col xl:max-w-[372px]'>
                <div className='mb-5 flex items-center justify-start gap-4'>
                  <span className='size-4 bg-[#77DFF9]' />
                  <p className={`w-full max-w-[528px] ${blackHanSans.variable} font-black_han_sans text-xl font-bold leading-5 text-[#77DFF9]`}>2</p>
                </div>

                <Image className='mb-5' src={imageStepTwo} alt={t('fortnite.second_step_image_alt')} quality={100} />
                <p className='mb-5 w-full max-w-[528px] font-bai_jamjuree text-base font-bold leading-4 text-[#77DFF9]'>{t('fortnite.second_step_title')}</p>
                <div className='my-5 flex items-start justify-start gap-4'>
                  <span className='size-4 bg-[#77DFF9]' />
                  <p className='w-full max-w-[528px] font-bai_jamjuree text-base font-medium leading-6 text-white'>{t('fortnite.second_step_text')}</p>
                </div>
              </div>

              <div className='flex w-full flex-col xl:max-w-[372px]'>
                <div className='mb-5 flex items-center justify-start gap-4'>
                  <span className='size-4 bg-[#77DFF9]' />
                  <p className={`w-full max-w-[528px] ${baiJamjuree.variable} font-black_han_sans text-xl font-bold leading-5 text-[#77DFF9]`}>3</p>
                </div>

                <Image className='mb-5' src={imageStepThree} alt={t('fortnite.third_step_image_alt')} />
                <p className='mb-5 w-full max-w-[528px] font-bai_jamjuree text-base font-bold leading-4 text-[#77DFF9]'>{t('fortnite.third_step_title')}</p>
                <div className='my-5 flex items-start justify-start gap-4'>
                  <span className='size-4 bg-[#77DFF9]' />
                  <p className='w-full max-w-[528px] font-bai_jamjuree text-base font-medium leading-6 text-white'>{t('fortnite.third_step_text')}</p>
                </div>
              </div>

              <div className='flex w-full flex-col xl:max-w-[372px]'>
                <div className='mb-5 flex items-center justify-start gap-4'>
                  <span className='size-4 bg-[#77DFF9]' />
                  <p className={`${bakbakOne.variable} w-full max-w-[528px] font-bakbak_one text-xl font-normal leading-5 text-[#77DFF9]`}>4</p>
                </div>

                <Image className='mb-5' src={imageStepFour} alt={t('fortnite.fourth_step_image_alt')} quality={100} />
                <p className='mb-5 w-full max-w-[528px] font-bai_jamjuree text-base font-bold leading-4 text-[#77DFF9]'>{t('fortnite.fourth_step_title')}</p>
                <div className='my-5 flex items-start justify-start gap-4'>
                  <span className='size-4 bg-[#77DFF9]' />
                  <p className='w-full max-w-[528px] font-bai_jamjuree text-base font-medium leading-6 text-white'>{t('fortnite.fourth_step_text')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Main>

      <Footer />
    </>
  );
}

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common', 'pages/fortnite', 'components/titles'])),
    },
  };
}
